import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Snackbar,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SyncIcon from "@mui/icons-material/Sync";
import { eventApi, IEvent } from "../../services/api/eventApi";
import { olympicApi, OlympicAdminRegistration } from "../../services/api/olympicApi";
import { OLYMPIC_REGIONS, OLYMPIC_TEAM_META, OlympicRegion, countQualifiedTeams, isTeamQualified } from "../../types/olympic";

const OlympicManagePage: React.FC = () => {
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [regionTab, setRegionTab] = useState<OlympicRegion>("SUDO");
  const [rows, setRows] = useState<OlympicAdminRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailRow, setDetailRow] = useState<OlympicAdminRegistration | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // 올림픽 이벤트만 로드
  useEffect(() => {
    const run = async () => {
      try {
        const all = await eventApi.getEvents();
        const olympicEvents = all.filter(
          (e) => e.event_Name?.includes("올림픽") && !e.event_Name?.includes("설명회")
        );
        setEvents(olympicEvents);
        if (olympicEvents.length > 0 && !selectedEventId) {
          setSelectedEventId(olympicEvents[0]._id);
        }
      } catch (err: any) {
        setError(err.message || "이벤트 목록을 불러오는데 실패했습니다.");
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!selectedEventId) return;
      setLoading(true);
      setError(null);
      try {
        const list = await olympicApi.getRegistrationsByEvent(selectedEventId);
        setRows(list);
      } catch (err: any) {
        setError(err.message || "등록 목록을 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [selectedEventId]);

  const regionRows = useMemo(() => rows.filter((r) => r.basicInfo?.region === regionTab), [rows, regionTab]);

  const regionStats = useMemo(() => {
    const map: Record<OlympicRegion, { churchCount: number; teamCount: number; total: number }> = {
      SUDO: { churchCount: 0, teamCount: 0, total: 0 },
      GYEONGSANG: { churchCount: 0, teamCount: 0, total: 0 },
      CHUNGJEON: { churchCount: 0, teamCount: 0, total: 0 },
    };
    rows.forEach((r) => {
      const region = r.basicInfo?.region as OlympicRegion | undefined;
      if (!region || !(region in map)) return;
      map[region].churchCount += 1;
      map[region].teamCount += countQualifiedTeams(r.teams || []);
      map[region].total += r.costs?.total || 0;
    });
    return map;
  }, [rows]);

  const handleDownloadRegion = () => {
    if (!selectedEventId) return;
    window.open(olympicApi.exportRegionExcelUrl(selectedEventId, regionTab), "_blank");
  };

  const handleDownloadChurch = (row: OlympicAdminRegistration) => {
    if (!selectedEventId || !row.churchId) return;
    window.open(olympicApi.exportChurchExcelUrl(selectedEventId, row.churchId), "_blank");
  };

  const handleDelete = async (row: OlympicAdminRegistration) => {
    if (!selectedEventId || !row.churchId) return;
    if (!window.confirm(`${row.basicInfo?.church_name} 등록을 삭제하시겠습니까?`)) return;
    try {
      await olympicApi.deleteRegistration(selectedEventId, row.churchId);
      setRows((prev) => prev.filter((r) => r.churchId !== row.churchId));
    } catch (err: any) {
      alert(err.message || "삭제 중 문제가 발생했습니다.");
    }
  };

  const handleSyncReceipts = async () => {
    if (!selectedEventId) return;
    if (!window.confirm("전체 올림픽 등록 데이터에 대해 영수증을 동기화하시겠습니까?")) return;
    try {
      setLoading(true);
      setError(null);
      const result = await olympicApi.syncReceipts(selectedEventId);
      if (result.success) {
        setSyncMessage(result.message || "영수증 동기화 완료");
      } else {
        setError("영수증 동기화에 실패했습니다.");
      }
    } catch (err: any) {
      setError("영수증 동기화 실패: " + (err.message || "알 수 없는 오류"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        올림픽 접수 관리
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 280 }} size="small">
          <InputLabel>이벤트 선택</InputLabel>
          <Select
            label="이벤트 선택"
            value={selectedEventId}
            onChange={(e) => setSelectedEventId(e.target.value)}
          >
            {events.map((ev) => (
              <MenuItem key={ev._id} value={ev._id}>
                {ev.event_Name} ({ev.event_Year})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {events.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            등록된 올림픽 이벤트가 없습니다.
          </Typography>
        )}
      </Paper>

      {/* 지역별 요약 카드 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {OLYMPIC_REGIONS.map((r) => {
          const s = regionStats[r.code];
          return (
            <Grid item xs={12} md={4} key={r.code}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600}>{r.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.date} · {r.place}</Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">교회 {s.churchCount}곳 / 팀 {s.teamCount}개</Typography>
                  <Typography variant="body2" color="primary">총액 {s.total.toLocaleString()}원</Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={regionTab} onChange={(_, v) => setRegionTab(v)} variant="fullWidth">
          {OLYMPIC_REGIONS.map((r) => (
            <Tab key={r.code} value={r.code} label={r.label} />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<SyncIcon />}
          onClick={handleSyncReceipts}
          disabled={!selectedEventId || loading}
        >
          영수증 동기화
        </Button>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadRegion}
          disabled={!selectedEventId || regionRows.length === 0}
        >
          이 지역 전체 엑셀 다운로드
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : regionRows.length === 0 ? (
        <Alert severity="info">이 지역에 등록된 교회가 없습니다.</Alert>
      ) : (
        <Grid container spacing={2}>
          {regionRows.map((row) => {
            const qualifiedTeams = (row.teams || []).filter(isTeamQualified);
            const teamSummary: Record<string, number> = {};
            qualifiedTeams.forEach((t) => {
              const label = OLYMPIC_TEAM_META[t.teamType]?.label || t.teamType;
              teamSummary[label] = (teamSummary[label] || 0) + 1;
            });

            return (
              <Grid item xs={12} md={6} lg={4} key={row.churchId || row._id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">{row.basicInfo?.church_name || row.churchName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      등록번호 {row.basicInfo?.church_number || row.churchCode}
                    </Typography>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, my: 1 }}>
                      {Object.entries(teamSummary).map(([label, count]) => (
                        <Chip key={label} label={`${label} × ${count}`} size="small" />
                      ))}
                    </Box>

                    <Typography variant="body2">
                      총 팀: {qualifiedTeams.length} · 선수 {qualifiedTeams.length * 10}명 · 코치 {qualifiedTeams.length * 2}명 · 참관자 {row.observerCount || 0}명
                    </Typography>
                    <Typography variant="body2" color="primary">
                      {row.costs?.total?.toLocaleString() || 0}원
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, gap: 0.5 }}>
                      <IconButton onClick={() => setDetailRow(row)} size="small"><VisibilityIcon fontSize="small" /></IconButton>
                      <IconButton onClick={() => handleDownloadChurch(row)} size="small"><DownloadIcon fontSize="small" /></IconButton>
                      <IconButton onClick={() => handleDelete(row)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {detailRow?.basicInfo?.church_name} — 참가 상세
        </DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Box>
              <Typography variant="subtitle2">기본 정보</Typography>
              <Typography variant="body2">참가지역: {OLYMPIC_REGIONS.find((r) => r.code === detailRow.basicInfo.region)?.label}</Typography>
              <Typography variant="body2">접수 담당자: {detailRow.basicInfo.receptionManager?.name} ({detailRow.basicInfo.receptionManager?.contact})</Typography>
              <Typography variant="body2">Sparks 감독관: {detailRow.basicInfo.sparksSupervisor?.name} ({detailRow.basicInfo.sparksSupervisor?.contact})</Typography>
              <Typography variant="body2">T&T 감독관: {detailRow.basicInfo.tntSupervisor?.name} ({detailRow.basicInfo.tntSupervisor?.contact})</Typography>
              <Typography variant="body2">담임 사역자: {detailRow.basicInfo.pastor?.name} ({detailRow.basicInfo.pastor?.contact})</Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">팀 구성</Typography>
              {(detailRow.teams || []).map((t) => (
                <Box key={t.id} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {OLYMPIC_TEAM_META[t.teamType]?.label} · {t.teamLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    코치: {t.coaches.map((c) => c.name).filter(Boolean).join(", ") || "-"}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                    {t.players
                      .filter((p) => p.name)
                      .map((p) => (
                        <Chip
                          key={p.index}
                          size="small"
                          label={`${p.isSubstitute ? "교체" : "#" + p.index} ${p.name}${p.gender ? ` (${p.gender === "F" ? "여" : "남"})` : ""}${p.grade ? ` ${p.grade}` : ""}`}
                        />
                      ))}
                  </Box>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2">금액</Typography>
              {(() => {
                const qt = (detailRow.teams || []).filter(isTeamQualified);
                return (
                  <Typography variant="body2">
                    팀 참가비 ({qt.length}팀 · 선수 {qt.length * 10}명 · 코치 {qt.length * 2}명): {detailRow.costs?.teamFee?.toLocaleString()}원
                  </Typography>
                );
              })()}
              <Typography variant="body2">참관자 ({detailRow.observerCount || 0}명): {detailRow.costs?.observers?.toLocaleString()}원</Typography>
              <Typography variant="body2">식사 (초등 {detailRow.mealCount?.elem || 0}명 / 중고등 {detailRow.mealCount?.midHigh || 0}명 / 일반 {detailRow.mealCount?.general || 0}명): {detailRow.costs?.meals?.toLocaleString()}원</Typography>
              <Typography variant="body2">캐노피 ({detailRow.canopyCount || 0}동): {detailRow.costs?.canopies?.toLocaleString()}원</Typography>
              <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                합계: {detailRow.costs?.total?.toLocaleString()}원
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!syncMessage}
        autoHideDuration={6000}
        onClose={() => setSyncMessage(null)}
        message={syncMessage}
      />
    </Container>
  );
};

export default OlympicManagePage;
